import type { CreateExperimentRequest, MoveExperimentRequest } from "@wallyo/contracts/console/experiments";
import type {
  AddLeadRequest,
  CreateCampaignRequest,
  MoveLeadRequest,
  SendCampaignRequest,
} from "@wallyo/contracts/console/growth";
import type { OpenIncidentRequest, UpdateIncidentRequest } from "@wallyo/contracts/console/incidents";
import type {
  ListJobsQuery,
  ListMarketplaceReviewsQuery,
  ListProvidersQuery,
} from "@wallyo/contracts/console/marketplace";
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { DefinedPermissionId } from "@wallyo/contracts/permissions";
import type {
  AssignRoleRequest,
  CreateRoleRequest,
  DeactivateStaffRequest,
  ReactivateStaffRequest,
  EditRoleRequest,
  EndStaffSessionsRequest,
  InviteStaffRequest,
  ListAuditLogRequest,
  RemoveRoleRequest,
  SetRoleFeatureRequest,
  SetRolePermissionRequest,
  SetStaffPasswordRequest,
  SignInStaffRequest,
  SignInStaffWithGoogleRequest,
  VerifyStaffCodeRequest,
} from "@wallyo/contracts/console";
import type {
  AddCandidateRequest,
  AddCourseRequest,
  ClosePayrollRequest,
  EditJobRequest,
  EnrolRequest,
  MoveCandidateRequest,
  OpenJobRequest,
  DecideTimeOffRequest,
  EditPayrollLineRequest,
  OpenPayrollRequest,
  OpenReviewCycleRequest,
  RequestTimeOffRequest,
  UpdateActionEnablementsRequest,
  WriteReviewRequest,
} from "@wallyo/contracts/console/hr";
import type { RevealContactDetailsRequest, SearchAccountsRequest } from "@wallyo/contracts/console/accounts";
import type { UpdateStaffSettingsRequest } from "@wallyo/contracts/console/dashboard";
import type {
  CreateDiscountRequest,
  CreatePromoRequest,
  EndDiscountRequest,
  EndPromoRequest,
  EndSubscriptionRequest,
  ExtendSubscriptionRequest,
  SetPlanPriceRequest,
} from "@wallyo/contracts/console/billing";
import type {
  AddCaseNoteRequest,
  ClaimSupportCaseRequest,
  CloseSupportCaseRequest,
  LinkCaseCustomerRequest,
  ListSupportCasesRequest,
  LogCaseCallRequest,
  OpenSupportCaseRequest,
  ReassignSupportCaseRequest,
  ReplyToCaseRequest,
} from "@wallyo/contracts/console/cases";
import type { DefinedRuleId } from "@wallyo/contracts/rules";
import type {
  CreateRuleVersionRequest,
  CreateTeamRequest,
  EditTeamRequest,
  FeatureSubject,
  PermissionRecord,
  RemoveCustomerFeatureValueRequest,
  SaveCustomerFeaturesRequest,
  SavePermissionsRequest,
  SaveStaffFeaturesRequest,
  SaveTeamMembersRequest,
  SaveUserSettingsRequest,
  SetCustomerFeatureValueRequest,
  StaffFeatureSubject,
  UserSettingsSubject,
} from "@wallyo/contracts/console/configuration";
import type { UserFeatureId } from "@wallyo/contracts/actions";
import type {
  ApproveVerificationRequest,
  ListVerificationsRequest,
  RejectVerificationRequest,
} from "@wallyo/contracts/console/verifications";
import type { GetHealthDashboardRequest } from "@wallyo/contracts/console/dashboard";
import type {
  ApprovePaymentRequestRequest,
  IssueCreditRequest,
  IssueRefundRequest,
  ListApprovalRequestsQuery,
  ListPaymentsQuery,
  MarkPaymentPaidRequest,
  RejectPaymentRequestRequest,
  SettleRefundRequest,
  WaiveFeeRequest,
} from "@wallyo/contracts/console/payments";
import type {
  AddReportEvidenceRequest,
  BanPermanentlyRequest,
  DecideAppealRequest,
  IssueVerdictRequest,
  LiftSuspensionRequest,
  PlaceLegalHoldRequest,
  ReferToPoliceRequest,
  ReleaseLegalHoldRequest,
  SafetyReportSeverity,
  SafetyReportStatus,
  SetReportSeverityRequest,
  ShareDataWithPoliceRequest,
  SuspendPendingInvestigationRequest,
  OpenSafetyReportRequest,
} from "@wallyo/contracts/console/safety";
import type { ConsoleClient, WriteOptions } from "./client";

/**
 * The console endpoints (shared/src/contracts/console-api.ts). Only these
 * exist today; every other screen waits for its own endpoint.
 */
export const CONSOLE_PATHS = {
  signIn: "/api/v1/console/sign-in",
  signInWithGoogle: "/api/v1/console/sign-in/google",
  verifyCode: "/api/v1/console/verify-code",
  signOut: "/api/v1/console/sign-out",
  me: "/api/v1/console/me",
  meSettings: "/api/v1/console/me/settings",
  auditLog: "/api/v1/console/audit-log",
  setPassword: "/api/v1/console/set-password",
  staff: "/api/v1/console/staff",
  roles: "/api/v1/console/roles",
  teams: "/api/v1/console/teams",
  directory: "/api/v1/console/directory",
  timeOff: "/api/v1/console/time-off",
  accessReviews: "/api/v1/console/access-reviews",
  payroll: "/api/v1/console/payroll",
  reviewCycles: "/api/v1/console/review-cycles",
  jobOpenings: "/api/v1/console/job-openings",
  candidates: "/api/v1/console/candidates",
  courses: "/api/v1/console/courses",
  reviews: "/api/v1/console/reviews",
  permissionHolders: "/api/v1/console/permissions/holders",
  accounts: "/api/v1/console/accounts",
  cases: "/api/v1/console/cases",
  accessRequests: "/api/v1/console/access-requests",
  features: "/api/v1/console/features",
  featureValues: "/api/v1/console/features/values",
  staffFeatures: "/api/v1/console/staff-features",
  staffActions: "/api/v1/console/staff-actions",
  actionEnablements: "/api/v1/console/action-enablements",
  overview: "/api/v1/console/overview",
  screenCounts: "/api/v1/console/screen-counts",
  incidents: "/api/v1/console/incidents",
  experiments: "/api/v1/console/experiments",
  leads: "/api/v1/console/leads",
  campaigns: "/api/v1/console/campaigns",
  referrals: "/api/v1/console/referrals",
  marketplace: "/api/v1/console/marketplace",
  marketplaceJobs: "/api/v1/console/marketplace/jobs",
  marketplaceProviders: "/api/v1/console/marketplace/providers",
  marketplaceCategories: "/api/v1/console/marketplace/categories",
  marketplaceReviews: "/api/v1/console/marketplace/reviews",
  staffFeatureValues: "/api/v1/console/staff-features/values",
  permissions: "/api/v1/console/permissions",
  permissionValues: "/api/v1/console/permissions/values",
  userSettings: "/api/v1/console/user-settings",
  userSettingValues: "/api/v1/console/user-settings/values",
  rules: "/api/v1/console/rules",
  verifications: "/api/v1/console/verifications",
  safetyReports: "/api/v1/console/safety/reports",
  payments: "/api/v1/console/payments",
  paymentsExport: "/api/v1/console/payments/export",
  paymentCredits: "/api/v1/console/payments/credits",
  approvalRequests: "/api/v1/console/approval-requests",
  dashboard: "/api/v1/console/dashboard",
  billingPrices: "/api/v1/console/billing/prices",
  billingPromos: "/api/v1/console/billing/promos",
  billingDiscounts: "/api/v1/console/billing/discounts",
  billingPros: "/api/v1/console/billing/pros",
  billingSubscriptions: "/api/v1/console/billing/subscriptions",
} as const;

const segment = (id: string) => encodeURIComponent(id);

/** The writes on one case: POST /console/cases/:supportCaseId/<write>. */
export type CaseWrite = "calls" | "claim" | "reassign" | "notes" | "replies" | "close" | "customer";

/** The writes on one report: POST /console/safety/reports/:safetyReportId/<write>. */
export type ReportWrite =
  | "evidence"
  | "severity"
  | "suspend"
  | "lift-suspension"
  | "verdict"
  | "ban"
  | "appeal-decision"
  | "police-referrals"
  | "legal-hold"
  | "legal-hold/release";

/** The corrections on one payment: POST /console/payments/:platformFeePaymentId/<write>. */
export type PaymentWrite = "refunds" | "waive" | "mark-paid";

/** Finance's writes on one subscription: POST /console/billing/subscriptions/:subscriptionId/<write>. */
export type SubscriptionWrite = "extend" | "end";

/** The paths that name a staff member or a role. */
export const consolePath = {
  deactivate: (staffMemberId: string) => `${CONSOLE_PATHS.staff}/${segment(staffMemberId)}/deactivate`,
  reactivate: (staffMemberId: string) => `${CONSOLE_PATHS.staff}/${segment(staffMemberId)}/reactivate`,
  endSessions: (staffMemberId: string) => `${CONSOLE_PATHS.staff}/${segment(staffMemberId)}/sessions/end`,
  memberRole: (staffMemberId: string, staffRoleId: string) =>
    `${CONSOLE_PATHS.staff}/${segment(staffMemberId)}/roles/${segment(staffRoleId)}`,
  role: (staffRoleId: string) => `${CONSOLE_PATHS.roles}/${segment(staffRoleId)}`,
  team: (staffTeamId: string) => `${CONSOLE_PATHS.teams}/${segment(staffTeamId)}`,
  directoryPerson: (uuid: number) => `${CONSOLE_PATHS.directory}/${uuid}`,
  jobOpening: (id: string) => `${CONSOLE_PATHS.jobOpenings}/${segment(id)}`,
  jobCandidates: (id: string) => `${CONSOLE_PATHS.jobOpenings}/${segment(id)}/candidates`,
  candidate: (id: string) => `${CONSOLE_PATHS.candidates}/${segment(id)}`,
  enrol: (id: string) => `${CONSOLE_PATHS.courses}/${segment(id)}/enrol`,
  review: (id: string) => `${CONSOLE_PATHS.reviews}/${segment(id)}`,
  payrollLine: (id: string) => `${CONSOLE_PATHS.payroll}/lines/${segment(id)}`,
  closePayroll: (id: string) => `${CONSOLE_PATHS.payroll}/${segment(id)}/close`,
  decideTimeOff: (id: string) => `${CONSOLE_PATHS.timeOff}/${segment(id)}/decide`,
  leaving: (staffMemberId: string) => `${CONSOLE_PATHS.staff}/${segment(staffMemberId)}/leaving`,
  teamMembers: (staffTeamId: string) => `${CONSOLE_PATHS.teams}/${segment(staffTeamId)}/members`,
  account: (userId: string) => `${CONSOLE_PATHS.accounts}/${segment(userId)}`,
  revealContact: (userId: string) => `${CONSOLE_PATHS.accounts}/${segment(userId)}/reveal-contact`,
  supportCase: (supportCaseId: string) => `${CONSOLE_PATHS.cases}/${segment(supportCaseId)}`,
  caseWrite: (supportCaseId: string, write: CaseWrite) => `${CONSOLE_PATHS.cases}/${segment(supportCaseId)}/${write}`,
  verification: (id: string) => `${CONSOLE_PATHS.verifications}/${segment(id)}`,
  verificationImages: (id: string) => `${CONSOLE_PATHS.verifications}/${segment(id)}/images`,
  approveVerification: (id: string) => `${CONSOLE_PATHS.verifications}/${segment(id)}/approve`,
  rejectVerification: (id: string) => `${CONSOLE_PATHS.verifications}/${segment(id)}/reject`,
  roleFeature: (staffRoleId: string, settingId: StaffFeatureId) =>
    `${CONSOLE_PATHS.roles}/${segment(staffRoleId)}/features/${segment(settingId)}`,
  rolePermission: (staffRoleId: string, settingId: DefinedPermissionId) =>
    `${CONSOLE_PATHS.roles}/${segment(staffRoleId)}/permissions/${segment(settingId)}`,
  featureValues: (settingId: UserFeatureId) => `${CONSOLE_PATHS.features}/${segment(settingId)}/values`,
  featureValue: (settingId: UserFeatureId, settingValueId: string) =>
    `${CONSOLE_PATHS.features}/${segment(settingId)}/values/${segment(settingValueId)}`,
  ruleVersions: (ruleId: DefinedRuleId) => `${CONSOLE_PATHS.rules}/${segment(ruleId)}/versions`,
  safetyReport: (id: string) => `${CONSOLE_PATHS.safetyReports}/${segment(id)}`,
  reportWrite: (id: string, write: ReportWrite) => `${CONSOLE_PATHS.safetyReports}/${segment(id)}/${write}`,
  policeDataShares: (id: string, policeReferralId: string) =>
    `${CONSOLE_PATHS.safetyReports}/${segment(id)}/police-referrals/${segment(policeReferralId)}/data-shares`,
  payment: (id: string) => `${CONSOLE_PATHS.payments}/${segment(id)}`,
  paymentWrite: (id: string, write: PaymentWrite) => `${CONSOLE_PATHS.payments}/${segment(id)}/${write}`,
  settleAdjustment: (paymentAdjustmentId: string) =>
    `${CONSOLE_PATHS.payments}/adjustments/${segment(paymentAdjustmentId)}/settle`,
  approvalDecision: (approvalRequestId: string, decision: "approve" | "reject") =>
    `${CONSOLE_PATHS.approvalRequests}/${segment(approvalRequestId)}/${decision}`,
  endPromo: (planPromoId: string) => `${CONSOLE_PATHS.billingPromos}/${segment(planPromoId)}/end`,
  endDiscount: (proDiscountId: string) => `${CONSOLE_PATHS.billingDiscounts}/${segment(proDiscountId)}/end`,
  proSubscriptions: (workerId: string) => `${CONSOLE_PATHS.billingPros}/${segment(workerId)}/subscriptions`,
  subscriptionWrite: (subscriptionId: string, write: SubscriptionWrite) =>
    `${CONSOLE_PATHS.billingSubscriptions}/${segment(subscriptionId)}/${write}`,
};

/** The order query parameters are written in, so the same request is always the same URL. */
const AUDIT_LOG_PARAMS = [
  "updaterUuid",
  "userUuid",
  "kind",
  "subjectUserId",
  "staffMemberId",
  "actionId",
  "area",
  "outcome",
  "effect",
  "from",
  "to",
  "cursor",
  "limit",
] as const satisfies readonly (keyof ListAuditLogRequest)[];

/**
 * `?a=1&b=2` for the parameters that are set, in the order `names` gives, so
 * the same request is always the same URL; "" when none is. Every list
 * endpoint's query goes through here.
 */
function orderedQuery<T extends object>(request: T, names: readonly (keyof T & string)[]): string {
  const params = new URLSearchParams();
  for (const name of names) {
    const value = request[name];
    if (value !== undefined && value !== "") params.set(name, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** `?a=1&b=2` for the filters that are set, or "" when none is. */
export function auditLogQuery(request: ListAuditLogRequest): string {
  return orderedQuery(request, AUDIT_LOG_PARAMS);
}

export function signInStaff(client: ConsoleClient, request: SignInStaffRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.signIn, request, "signInStaffResponse", options);
}

/**
 * Sign in with a Google Workspace account (WALLIYO-0120). The ID token comes
 * from Google Identity Services; everything that decides whether it is accepted
 * happens on the server.
 */
export function signInStaffWithGoogle(
  client: ConsoleClient,
  request: SignInStaffWithGoogleRequest,
  options?: WriteOptions,
) {
  return client.post(CONSOLE_PATHS.signInWithGoogle, request, "signInStaffWithGoogleResponse", options);
}

export function verifyStaffCode(client: ConsoleClient, request: VerifyStaffCodeRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.verifyCode, request, "verifyStaffCodeResponse", options);
}

export function signOutStaff(client: ConsoleClient, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.signOut, {}, "signOutStaffResponse", options);
}

export function getStaffMe(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.me, "getStaffMeResponse");
}

/**
 * The signed-in staff member's own console settings (WALLIYO-0122): their
 * language and theme. The endpoint has existed since the settings work and had
 * no caller, so the language picker only changed the current tab and the theme
 * could not be changed at all.
 */
export function updateStaffSettings(
  client: ConsoleClient,
  request: UpdateStaffSettingsRequest,
  options?: WriteOptions,
) {
  return client.write("PUT", CONSOLE_PATHS.meSettings, request, "updateStaffSettingsResponse", options);
}

/** One page of the audit log. Reading it is itself audited by the server. */
export function listAuditLog(client: ConsoleClient, request: ListAuditLogRequest = {}) {
  return client.get(`${CONSOLE_PATHS.auditLog}${auditLogQuery(request)}`, "listAuditLogResponse");
}

// ── Rôles & équipe ────────────────────────────────────────────────────────

/** Public: an invited staff member chooses their first password with the SMS code. */
export function setStaffPassword(client: ConsoleClient, request: SetStaffPasswordRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.setPassword, request, "setStaffPasswordResponse", options);
}

export function listStaff(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.staff, "listStaffResponse");
}

export function inviteStaff(client: ConsoleClient, request: InviteStaffRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.staff, request, "inviteStaffResponse", options);
}

export function deactivateStaff(
  client: ConsoleClient,
  staffMemberId: string,
  request: DeactivateStaffRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.deactivate(staffMemberId), request, "deactivateStaffResponse", options);
}

/** Puts a deactivated member back (WALLIYO-0121). Same feature as deactivating. */
export function reactivateStaff(
  client: ConsoleClient,
  staffMemberId: string,
  request: ReactivateStaffRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.reactivate(staffMemberId), request, "reactivateStaffResponse", options);
}

export function endStaffSessions(
  client: ConsoleClient,
  staffMemberId: string,
  request: EndStaffSessionsRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.endSessions(staffMemberId), request, "endStaffSessionsResponse", options);
}

export function assignStaffRole(
  client: ConsoleClient,
  staffMemberId: string,
  staffRoleId: string,
  request: AssignRoleRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.memberRole(staffMemberId, staffRoleId), request, "assignRoleResponse", options);
}

export function removeStaffRole(
  client: ConsoleClient,
  staffMemberId: string,
  staffRoleId: string,
  request: RemoveRoleRequest,
  options?: WriteOptions,
) {
  return client.write(
    "DELETE",
    consolePath.memberRole(staffMemberId, staffRoleId),
    request,
    "removeRoleResponse",
    options,
  );
}

// ── Human Resources: the directory (WALLIYO-0131) ─────────────────────────
// No feature: anybody signed in can look anybody up. An empty search is the
// whole directory, which is what the tab opens on.

export function listDirectory(client: ConsoleClient, query = "") {
  const search = query.trim() === "" ? "" : `?${new URLSearchParams({ q: query.trim() }).toString()}`;
  return client.get(`${CONSOLE_PATHS.directory}${search}`, "listDirectoryResponse");
}

export function getDirectoryPerson(client: ConsoleClient, uuid: number) {
  return client.get(consolePath.directoryPerson(uuid), "getDirectoryPersonResponse");
}

/** What deactivating does not do, read before the button is pressed. */
export function getLeaving(client: ConsoleClient, staffMemberId: string) {
  return client.get(consolePath.leaving(staffMemberId), "getLeavingResponse");
}

// ── Human Resources: time off (WALLIYO-0131) ──────────────────────────────

export function listJobOpenings(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.jobOpenings, "listJobOpeningsResponse");
}

export function openJob(client: ConsoleClient, request: OpenJobRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.jobOpenings, request, "openJobResponse", options);
}

export function editJob(client: ConsoleClient, jobOpeningId: string, request: EditJobRequest, options?: WriteOptions) {
  return client.write("PATCH", consolePath.jobOpening(jobOpeningId), request, "editJobResponse", options);
}

export function addCandidate(
  client: ConsoleClient,
  jobOpeningId: string,
  request: AddCandidateRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.jobCandidates(jobOpeningId), request, "addCandidateResponse", options);
}

export function moveCandidate(
  client: ConsoleClient,
  jobCandidateId: string,
  request: MoveCandidateRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.candidate(jobCandidateId), request, "moveCandidateResponse", options);
}

export function listCourses(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.courses, "listCoursesResponse");
}

export function addCourse(client: ConsoleClient, request: AddCourseRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.courses, request, "addCourseResponse", options);
}

export function enrolInCourse(client: ConsoleClient, courseId: string, request: EnrolRequest, options?: WriteOptions) {
  return client.post(consolePath.enrol(courseId), request, "enrolResponse", options);
}

export function getReviewCycle(client: ConsoleClient, reviewCycleId = "") {
  const search = reviewCycleId === "" ? "" : `?${new URLSearchParams({ cycle: reviewCycleId }).toString()}`;
  return client.get(`${CONSOLE_PATHS.reviewCycles}${search}`, "getReviewCycleResponse");
}

export function openReviewCycle(client: ConsoleClient, request: OpenReviewCycleRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.reviewCycles, request, "openReviewCycleResponse", options);
}

export function writeReview(
  client: ConsoleClient,
  performanceReviewId: string,
  request: WriteReviewRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.review(performanceReviewId), request, "writeReviewResponse", options);
}

export function getPayroll(client: ConsoleClient, month = "") {
  const search = month === "" ? "" : `?${new URLSearchParams({ month }).toString()}`;
  return client.get(`${CONSOLE_PATHS.payroll}${search}`, "getPayrollResponse");
}

export function openPayroll(client: ConsoleClient, request: OpenPayrollRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.payroll, request, "openPayrollResponse", options);
}

export function editPayrollLine(
  client: ConsoleClient,
  payrollLineId: string,
  request: EditPayrollLineRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.payrollLine(payrollLineId), request, "editPayrollLineResponse", options);
}

export function closePayroll(
  client: ConsoleClient,
  payrollRunId: string,
  request: ClosePayrollRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.closePayroll(payrollRunId), request, "closePayrollResponse", options);
}

/** Every action, for one employee or one role, and where each answer came from. */
export function listStaffActions(client: ConsoleClient, subject: { uuid: number } | { staffRoleId: string }) {
  const search = new URLSearchParams(
    "uuid" in subject ? { uuid: String(subject.uuid) } : { staffRoleId: subject.staffRoleId },
  ).toString();
  return client.get(`${CONSOLE_PATHS.staffActions}?${search}`, "listStaffActionsResponse");
}

/** Turn actions on or off for one employee or one role, or clear them back to inherited. */
export function updateActionEnablements(
  client: ConsoleClient,
  request: UpdateActionEnablementsRequest,
  options?: WriteOptions,
) {
  return client.post(CONSOLE_PATHS.actionEnablements, request, "updateActionEnablementsResponse", options);
}

export function listAccessReviews(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.accessReviews, "listAccessReviewsResponse");
}

export function listTimeOff(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.timeOff, "listTimeOffResponse");
}

export function requestTimeOff(client: ConsoleClient, request: RequestTimeOffRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.timeOff, request, "requestTimeOffResponse", options);
}

export function decideTimeOff(
  client: ConsoleClient,
  timeOffRequestId: string,
  request: DecideTimeOffRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.decideTimeOff(timeOffRequestId), request, "decideTimeOffResponse", options);
}

// ── Teams (WALLIYO-0131) ──────────────────────────────────────────────────

export function listTeams(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.teams, "listTeamsResponse");
}

export function getTeam(client: ConsoleClient, staffTeamId: string) {
  return client.get(consolePath.team(staffTeamId), "getTeamResponse");
}

export function createTeam(client: ConsoleClient, request: CreateTeamRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.teams, request, "createTeamResponse", options);
}

export function editTeam(client: ConsoleClient, staffTeamId: string, request: EditTeamRequest, options?: WriteOptions) {
  return client.write("PATCH", consolePath.team(staffTeamId), request, "editTeamResponse", options);
}

export function saveTeamMembers(
  client: ConsoleClient,
  staffTeamId: string,
  request: SaveTeamMembersRequest,
  options?: WriteOptions,
) {
  return client.write("PUT", consolePath.teamMembers(staffTeamId), request, "saveTeamMembersResponse", options);
}

export function listRoles(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.roles, "listRolesResponse");
}

export function createRole(client: ConsoleClient, request: CreateRoleRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.roles, request, "createRoleResponse", options);
}

export function editRole(client: ConsoleClient, staffRoleId: string, request: EditRoleRequest, options?: WriteOptions) {
  return client.write("PATCH", consolePath.role(staffRoleId), request, "editRoleResponse", options);
}

export function setRoleFeature(
  client: ConsoleClient,
  staffRoleId: string,
  settingId: StaffFeatureId,
  request: SetRoleFeatureRequest,
  options?: WriteOptions,
) {
  return client.write(
    "PUT",
    consolePath.roleFeature(staffRoleId, settingId),
    request,
    "setRoleFeatureResponse",
    options,
  );
}

/**
 * Grants, revokes or re-limits one permission on a role (ADR-0019). The role
 * must already grant the feature it sits on, and the limits sent are the whole
 * set, one per currency; left out, they stay as they are.
 */
export function setRolePermission(
  client: ConsoleClient,
  staffRoleId: string,
  settingId: DefinedPermissionId,
  request: SetRolePermissionRequest,
  options?: WriteOptions,
) {
  return client.write(
    "PUT",
    consolePath.rolePermission(staffRoleId, settingId),
    request,
    "setRolePermissionResponse",
    options,
  );
}

/** Who can take this staff action now, through which roles and with what limits. A plain read. */
export function listPermissionHolders(client: ConsoleClient, actionId: string) {
  return client.get(
    `${CONSOLE_PATHS.permissionHolders}?actionId=${encodeURIComponent(actionId)}`,
    "listPermissionHoldersResponse",
  );
}

// ── Clients & pros ────────────────────────────────────────────────────────

/** `?query=…&limit=…&cursor=…`, in that order, leaving out what isn't set. */
export function accountSearchQuery(request: SearchAccountsRequest): string {
  const params = new URLSearchParams({ query: request.query });
  if (request.limit !== undefined) params.set("limit", String(request.limit));
  if (request.cursor) params.set("cursor", request.cursor);
  return `?${params.toString()}`;
}

/** One page of accounts matching a name, phone, email or account id. Phones and emails come masked. */
export function searchAccounts(client: ConsoleClient, request: SearchAccountsRequest) {
  return client.get(`${CONSOLE_PATHS.accounts}${accountSearchQuery(request)}`, "searchAccountsResponse");
}

/** A customer's account page, contact details masked. */
export function getCustomerAccount(client: ConsoleClient, userId: string) {
  return client.get(consolePath.account(userId), "getCustomerAccountResponse");
}

/**
 * Their contact details in full. Audited by the server with the reason; never
 * retried by itself. The endpoint takes no Idempotency-Key (a replay would
 * have to store the details): each click is its own reveal, audited on its own.
 */
export function revealContactDetails(client: ConsoleClient, userId: string, request: RevealContactDetailsRequest) {
  return client.post(consolePath.revealContact(userId), request, "revealContactDetailsResponse");
}

// ── Dossiers ──────────────────────────────────────────────────────────────

/** The order the queue's parameters are written in, so the same queue is always the same URL. */
const SUPPORT_CASES_PARAMS = [
  "assigned",
  "late",
  "status",
  "channel",
  "userId",
  "cursor",
  "limit",
] as const satisfies readonly (keyof ListSupportCasesRequest)[];

/** `?assigned=mine&late=true…` for the filters that are set, or "" when none is. */
export function supportCasesQuery(request: ListSupportCasesRequest): string {
  return orderedQuery(request, SUPPORT_CASES_PARAMS);
}

/** One page of the queue: unassigned first, then the earliest reply deadline. */
// ── Access requests (WALLIYO-0131) ───────────────────────────────────────

/** Ask for a permission you do not hold. It opens a case for whoever can grant it. */
export function askForPermission(
  client: ConsoleClient,
  request: { settingId: string; until?: string | null; reason: string },
  options?: WriteOptions,
) {
  return client.post(CONSOLE_PATHS.accessRequests, request, "askForPermissionResponse", options);
}

/** What you have asked for, and where it got to. Needs no feature: it is your own access. */
export function listMyAccessRequests(client: ConsoleClient) {
  return client.get(`${CONSOLE_PATHS.accessRequests}/mine`, "listMyAccessRequestsResponse");
}

/** Answer one. Refusing is a normal answer and is recorded like a grant. */
export function decideAccessRequest(
  client: ConsoleClient,
  supportCaseId: string,
  request: { granted: boolean; reason: string },
  options?: WriteOptions,
) {
  return client.post(
    `${CONSOLE_PATHS.accessRequests}/${segment(supportCaseId)}/decide`,
    request,
    "decideAccessRequestResponse",
    options,
  );
}

export function listSupportCases(client: ConsoleClient, request: ListSupportCasesRequest = {}) {
  return client.get(`${CONSOLE_PATHS.cases}${supportCasesQuery(request)}`, "listSupportCasesResponse");
}

/** One case and its whole timeline, internal notes included. */
export function getSupportCase(client: ConsoleClient, supportCaseId: string) {
  return client.get(consolePath.supportCase(supportCaseId), "getSupportCaseResponse");
}

/** Logs a phone call and opens a case for it; the caller owns it. */
export function openSupportCase(client: ConsoleClient, request: OpenSupportCaseRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.cases, request, "openSupportCaseResponse", options);
}

export function logCaseCall(
  client: ConsoleClient,
  supportCaseId: string,
  request: LogCaseCallRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "calls"), request, "logCaseCallResponse", options);
}

/** Only one agent wins: the others are told the case is already claimed. */
export function claimSupportCase(
  client: ConsoleClient,
  supportCaseId: string,
  request: ClaimSupportCaseRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "claim"), request, "claimSupportCaseResponse", options);
}

export function reassignSupportCase(
  client: ConsoleClient,
  supportCaseId: string,
  request: ReassignSupportCaseRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "reassign"), request, "reassignSupportCaseResponse", options);
}

/** An internal note: never shown to the customer. */
export function addCaseNote(
  client: ConsoleClient,
  supportCaseId: string,
  request: AddCaseNoteRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "notes"), request, "addCaseNoteResponse", options);
}

/** A reply to the customer, in the app or by SMS. Only the case's owner replies. */
export function replyToCase(
  client: ConsoleClient,
  supportCaseId: string,
  request: ReplyToCaseRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "replies"), request, "replyToCaseResponse", options);
}

export function closeSupportCase(
  client: ConsoleClient,
  supportCaseId: string,
  request: CloseSupportCaseRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "close"), request, "closeSupportCaseResponse", options);
}

/** Says who the caller is: links the case to a customer account. */
export function linkCaseCustomer(
  client: ConsoleClient,
  supportCaseId: string,
  request: LinkCaseCustomerRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.caseWrite(supportCaseId, "customer"), request, "linkCaseCustomerResponse", options);
}

// ── Fonctionnalités ───────────────────────────────────────────────────────

/**
 * Whose features to show, and what each one says for them.
 *
 * The subject travels as `?for=`, with `?uuid=` when it is one user. No
 * subject means all clients: an empty UUID box opens on everyone rather than
 * failing.
 */
export function featureSubjectQuery(subject: FeatureSubject): string {
  return subject.kind === "user"
    ? orderedQuery({ for: "user", uuid: subject.uuid }, ["for", "uuid"])
    : orderedQuery({ for: subject.kind }, ["for"]);
}

/** Every customer feature enablement, by category then id, resolved for one subject. */
export function listCustomerFeatures(client: ConsoleClient, subject: FeatureSubject) {
  return client.get(`${CONSOLE_PATHS.features}${featureSubjectQuery(subject)}`, "listCustomerFeaturesResponse");
}

/**
 * One Save: every toggle that moved, in one transaction. The server writes
 * all of them or none, and audits each one separately.
 */
export function saveCustomerFeatures(
  client: ConsoleClient,
  request: SaveCustomerFeaturesRequest,
  options?: WriteOptions,
) {
  return client.write("PUT", CONSOLE_PATHS.featureValues, request, "saveCustomerFeaturesResponse", options);
}

/** One feature and every value set for it, broadest scope first. */
export function listCustomerFeatureValues(client: ConsoleClient, settingId: UserFeatureId) {
  return client.get(consolePath.featureValues(settingId), "listCustomerFeatureValuesResponse");
}

/** Sets the value at one scope and target: without `version` it creates, with it it changes that value. */
export function setCustomerFeatureValue(
  client: ConsoleClient,
  settingId: UserFeatureId,
  request: SetCustomerFeatureValueRequest,
  options?: WriteOptions,
) {
  return client.write("PUT", consolePath.featureValues(settingId), request, "setCustomerFeatureValueResponse", options);
}

/** Removes a value, so the next broader scope (or the default) applies again. */
export function removeCustomerFeatureValue(
  client: ConsoleClient,
  settingId: UserFeatureId,
  settingValueId: string,
  request: RemoveCustomerFeatureValueRequest,
  options?: WriteOptions,
) {
  return client.write(
    "DELETE",
    consolePath.featureValue(settingId, settingValueId),
    request,
    "removeCustomerFeatureValueResponse",
    options,
  );
}

// ── Feature Enablements, Walliyo console ─────────────────────────────────

/** Every console feature enablement, resolved for one employee. */
export function listStaffFeatures(client: ConsoleClient, subject: StaffFeatureSubject) {
  return client.get(
    `${CONSOLE_PATHS.staffFeatures}${orderedQuery({ uuid: subject.uuid }, ["uuid"])}`,
    "listStaffFeaturesResponse",
  );
}

/** One Save: every console toggle that moved, in one transaction. */
export function saveStaffFeatures(client: ConsoleClient, request: SaveStaffFeaturesRequest, options?: WriteOptions) {
  return client.write("PUT", CONSOLE_PATHS.staffFeatureValues, request, "saveStaffFeaturesResponse", options);
}

// ── Permissions ──────────────────────────────────────────────────────────

/** What a role or one employee may do alone, and their ceilings. */
export function listPermissions(client: ConsoleClient, record: PermissionRecord) {
  const query =
    record.kind === "role"
      ? orderedQuery({ for: "role", staffRoleId: record.staffRoleId }, ["for", "staffRoleId"])
      : orderedQuery({ for: "employee", uuid: record.uuid }, ["for", "uuid"]);
  return client.get(`${CONSOLE_PATHS.permissions}${query}`, "listPermissionsResponse");
}

/** One Save: every grant that moved, in one transaction. */
export function savePermissions(client: ConsoleClient, request: SavePermissionsRequest, options?: WriteOptions) {
  return client.write("PUT", CONSOLE_PATHS.permissionValues, request, "savePermissionsResponse", options);
}

// ── User Settings ────────────────────────────────────────────────────────

/** One person's own settings: a customer's, or an employee's console ones. */
export function listUserSettings(client: ConsoleClient, subject: UserSettingsSubject) {
  return client.get(
    `${CONSOLE_PATHS.userSettings}${orderedQuery({ for: subject.audience, uuid: subject.uuid }, ["for", "uuid"])}`,
    "listUserSettingsResponse",
  );
}

/** One Save: every setting that moved, in one transaction. */
export function saveUserSettings(client: ConsoleClient, request: SaveUserSettingsRequest, options?: WriteOptions) {
  return client.write("PUT", CONSOLE_PATHS.userSettingValues, request, "saveUserSettingsResponse", options);
}

// ── Règles ────────────────────────────────────────────────────────────────

/** Every rule, with what applies now and the next scheduled version. */
export function listRules(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.rules, "listRulesResponse");
}

/** One rule and its versions, newest version number first. */
export function listRuleVersions(client: ConsoleClient, ruleId: DefinedRuleId) {
  return client.get(consolePath.ruleVersions(ruleId), "listRuleVersionsResponse");
}

/** Adds a version, effective now or later. Versions are only ever added. */
export function createRuleVersion(
  client: ConsoleClient,
  ruleId: DefinedRuleId,
  request: CreateRuleVersionRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.ruleVersions(ruleId), request, "createRuleVersionResponse", options);
}

// ── Vérifications ─────────────────────────────────────────────────────────

const VERIFICATIONS_PARAMS = [
  "status",
  "documentType",
  "country",
  "cursor",
  "limit",
] as const satisfies readonly (keyof ListVerificationsRequest)[];

/** `?status=…&documentType=…`, in a fixed order, leaving out what isn't set. */
export function verificationsQuery(request: ListVerificationsRequest): string {
  return orderedQuery(request, VERIFICATIONS_PARAMS);
}

/** The queue, oldest first. Needs REVIEW_VERIFICATIONS. */
export function listVerifications(client: ConsoleClient, request: ListVerificationsRequest = {}) {
  return client.get(`${CONSOLE_PATHS.verifications}${verificationsQuery(request)}`, "listVerificationsResponse");
}

/** One verification: last 4 characters, banned-document match, the pro's standing, earlier attempts. No images. */
export function getVerification(client: ConsoleClient, identityVerificationId: string) {
  return client.get(consolePath.verification(identityVerificationId), "getVerificationResponse");
}

/**
 * Signed URLs to the ID images, valid ID_IMAGE_URL_TTL_SECONDS. Needs
 * VIEW_ID_IMAGES, and every call is audited by the server as a sensitive
 * read: it is only ever called when the reviewer asks to see the documents.
 */
export function getVerificationImages(client: ConsoleClient, identityVerificationId: string) {
  return client.get(consolePath.verificationImages(identityVerificationId), "getVerificationImagesResponse");
}

/** Needs REVIEW_VERIFICATIONS and APPROVE_PROS. Refused for a banned document. */
export function approveVerification(
  client: ConsoleClient,
  identityVerificationId: string,
  request: ApproveVerificationRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.approveVerification(identityVerificationId),
    request,
    "approveVerificationResponse",
    options,
  );
}

/** Needs REVIEW_VERIFICATIONS. The reason is sent to the pro by SMS. */
export function rejectVerification(
  client: ConsoleClient,
  identityVerificationId: string,
  request: RejectVerificationRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.rejectVerification(identityVerificationId),
    request,
    "rejectVerificationResponse",
    options,
  );
}
// ── Signalements ──────────────────────────────────────────────────────────

/** GET /console/safety/reports's filters (server/http/validation/safety.ts). */
export type ListSafetyReportsQuery = {
  severity?: SafetyReportSeverity;
  status?: SafetyReportStatus;
  subjectUserId?: string;
  /** Only reports with an interim suspension past its review deadline. */
  reviewOverdue?: boolean;
  limit?: number;
};

const SAFETY_REPORTS_PARAMS = [
  "severity",
  "status",
  "subjectUserId",
  "reviewOverdue",
  "limit",
] as const satisfies readonly (keyof ListSafetyReportsQuery)[];

export function safetyReportsQuery(query: ListSafetyReportsQuery): string {
  return orderedQuery(query, SAFETY_REPORTS_PARAMS);
}

/** The reports. Needs HANDLE_REPORTS (or another safety feature). */
export function listSafetyReports(client: ConsoleClient, query: ListSafetyReportsQuery = {}) {
  return client.get(`${CONSOLE_PATHS.safetyReports}${safetyReportsQuery(query)}`, "listSafetyReportsResponse");
}

/** One report: the timeline, restrictions, police referrals, legal holds and related reports. */
export function getSafetyReport(client: ConsoleClient, safetyReportId: string) {
  return client.get(consolePath.safetyReport(safetyReportId), "getSafetyReportResponse");
}

/**
 * Open a safety report from the console (WALLIYO-0122): a complaint that
 * arrived by phone or from a case, rather than through the app's own report
 * button. The endpoint has existed since the safety work; nothing called it.
 */
export function openSafetyReport(client: ConsoleClient, request: OpenSafetyReportRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.safetyReports, request, "openSafetyReportResponse", options);
}

export function addReportEvidence(
  client: ConsoleClient,
  safetyReportId: string,
  request: AddReportEvidenceRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "evidence"),
    request,
    "addReportEvidenceResponse",
    options,
  );
}

export function setReportSeverity(
  client: ConsoleClient,
  safetyReportId: string,
  request: SetReportSeverityRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "severity"),
    request,
    "setReportSeverityResponse",
    options,
  );
}

/** Suspends the reported person until a staff decision; the review deadline is the rule's, never an expiry. */
export function suspendPendingInvestigation(
  client: ConsoleClient,
  safetyReportId: string,
  request: SuspendPendingInvestigationRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "suspend"),
    request,
    "suspendPendingInvestigationResponse",
    options,
  );
}

export function liftSuspension(
  client: ConsoleClient,
  safetyReportId: string,
  request: LiftSuspensionRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "lift-suspension"),
    request,
    "liftSuspensionResponse",
    options,
  );
}

export function issueVerdict(
  client: ConsoleClient,
  safetyReportId: string,
  request: IssueVerdictRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.reportWrite(safetyReportId, "verdict"), request, "issueVerdictResponse", options);
}

export function banPermanently(
  client: ConsoleClient,
  safetyReportId: string,
  request: BanPermanentlyRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.reportWrite(safetyReportId, "ban"), request, "banPermanentlyResponse", options);
}

/** Decided by someone other than whoever issued the verdict: the server refuses otherwise. */
export function decideAppeal(
  client: ConsoleClient,
  safetyReportId: string,
  request: DecideAppealRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "appeal-decision"),
    request,
    "decideAppealResponse",
    options,
  );
}

export function referToPolice(
  client: ConsoleClient,
  safetyReportId: string,
  request: ReferToPoliceRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "police-referrals"),
    request,
    "referToPoliceResponse",
    options,
  );
}

/** An Owner only. Records exactly what was handed to the police. */
export function shareDataWithPolice(
  client: ConsoleClient,
  safetyReportId: string,
  policeReferralId: string,
  request: ShareDataWithPoliceRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.policeDataShares(safetyReportId, policeReferralId),
    request,
    "shareDataWithPoliceResponse",
    options,
  );
}

export function placeLegalHold(
  client: ConsoleClient,
  safetyReportId: string,
  request: PlaceLegalHoldRequest,
  options?: WriteOptions,
) {
  return client.post(consolePath.reportWrite(safetyReportId, "legal-hold"), request, "placeLegalHoldResponse", options);
}

export function releaseLegalHold(
  client: ConsoleClient,
  safetyReportId: string,
  request: ReleaseLegalHoldRequest,
  options?: WriteOptions,
) {
  return client.post(
    consolePath.reportWrite(safetyReportId, "legal-hold/release"),
    request,
    "releaseLegalHoldResponse",
    options,
  );
}

// ── Paiements ─────────────────────────────────────────────────────────────

const PAYMENTS_PARAMS = [
  "workerId",
  "provider",
  "status",
  "from",
  "to",
  "likelyDuplicates",
  "cursor",
  "limit",
] as const satisfies readonly (keyof ListPaymentsQuery)[];

export function paymentsQuery(query: ListPaymentsQuery): string {
  return orderedQuery(query, PAYMENTS_PARAMS);
}

/** One page of fee payments, newest first. Needs VIEW_PAYMENTS. */
export function listPayments(client: ConsoleClient, query: ListPaymentsQuery = {}) {
  return client.get(`${CONSOLE_PATHS.payments}${paymentsQuery(query)}`, "listPaymentsResponse");
}

/** One payment with its corrections, what can still be refunded, and the pro's credit. */
export function getPayment(client: ConsoleClient, platformFeePaymentId: string) {
  return client.get(consolePath.payment(platformFeePaymentId), "getPaymentResponse");
}

/** Money writes refuse to run without an Idempotency-Key: one per click, so it is required here. */
export function issueRefund(
  client: ConsoleClient,
  platformFeePaymentId: string,
  request: IssueRefundRequest,
  options: Required<WriteOptions>,
) {
  return client.post(
    consolePath.paymentWrite(platformFeePaymentId, "refunds"),
    request,
    "issueRefundResponse",
    options,
  );
}

export function waiveFee(
  client: ConsoleClient,
  platformFeePaymentId: string,
  request: WaiveFeeRequest,
  options: Required<WriteOptions>,
) {
  return client.post(consolePath.paymentWrite(platformFeePaymentId, "waive"), request, "waiveFeeResponse", options);
}

export function markPaymentPaid(
  client: ConsoleClient,
  platformFeePaymentId: string,
  request: MarkPaymentPaidRequest,
  options: Required<WriteOptions>,
) {
  return client.post(
    consolePath.paymentWrite(platformFeePaymentId, "mark-paid"),
    request,
    "markPaymentPaidResponse",
    options,
  );
}

export function issueCredit(client: ConsoleClient, request: IssueCreditRequest, options: Required<WriteOptions>) {
  return client.post(CONSOLE_PATHS.paymentCredits, request, "issueCreditResponse", options);
}

/** How a pending or manual refund ended, recorded once by Finance. */
export function settleRefund(
  client: ConsoleClient,
  paymentAdjustmentId: string,
  request: SettleRefundRequest,
  options: Required<WriteOptions>,
) {
  return client.post(consolePath.settleAdjustment(paymentAdjustmentId), request, "settleRefundResponse", options);
}

/** Refunds and waivers above the limit, oldest pending first. */
export function listApprovalRequests(client: ConsoleClient, query: ListApprovalRequestsQuery = {}) {
  const suffix = query.status ? `?status=${encodeURIComponent(query.status)}` : "";
  return client.get(`${CONSOLE_PATHS.approvalRequests}${suffix}`, "listApprovalRequestsResponse");
}

/** An Owner, never the requester. */
export function approvePaymentRequest(
  client: ConsoleClient,
  approvalRequestId: string,
  request: ApprovePaymentRequestRequest,
  options: Required<WriteOptions>,
) {
  return client.post(
    consolePath.approvalDecision(approvalRequestId, "approve"),
    request,
    "approvePaymentRequestResponse",
    options,
  );
}

export function rejectPaymentRequest(
  client: ConsoleClient,
  approvalRequestId: string,
  request: RejectPaymentRequestRequest,
  options: Required<WriteOptions>,
) {
  return client.post(
    consolePath.approvalDecision(approvalRequestId, "reject"),
    request,
    "rejectPaymentRequestResponse",
    options,
  );
}

/** `?month=2026-09&reason=…`: the month's CSV. Audited by the server as a sensitive read. */
export function paymentsExportPath(month: string, reason?: string): string {
  const params = new URLSearchParams({ month });
  if (reason?.trim()) params.set("reason", reason.trim());
  return `${CONSOLE_PATHS.paymentsExport}?${params.toString()}`;
}

export function exportPayments(client: ConsoleClient, month: string, reason?: string) {
  return client.getFile(paymentsExportPath(month, reason));
}

// ── Tarifs et abonnements ─────────────────────────────────────────────────
//
// Reads need VIEW_PAYMENTS; each write has its own staff feature, names the
// `version` the screen showed where there is one, and must carry an
// Idempotency-Key, one per click (ADR-0018 §4a).

/** Every plan staff can price, and every price version, newest first. */
export function listPlanPrices(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.billingPrices, "listPlanPricesResponse");
}

/** MANAGE_PLAN_PRICES. A new version: it applies to new subscriptions only. */
export function setPlanPrice(client: ConsoleClient, request: SetPlanPriceRequest, options: Required<WriteOptions>) {
  return client.post(CONSOLE_PATHS.billingPrices, request, "setPlanPriceResponse", options);
}

export function listPromos(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.billingPromos, "listPromosResponse");
}

/** RUN_PROMOS. */
export function createPromo(client: ConsoleClient, request: CreatePromoRequest, options: Required<WriteOptions>) {
  return client.post(CONSOLE_PATHS.billingPromos, request, "createPromoResponse", options);
}

/** RUN_PROMOS, at the version on screen. Pros already on the promo keep it. */
export function endPromo(
  client: ConsoleClient,
  planPromoId: string,
  request: EndPromoRequest,
  options: Required<WriteOptions>,
) {
  return client.post(consolePath.endPromo(planPromoId), request, "endPromoResponse", options);
}

export function listDiscounts(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.billingDiscounts, "listDiscountsResponse");
}

/** OFFER_DISCOUNTS. Exactly one target, matching the scope. */
export function createDiscount(client: ConsoleClient, request: CreateDiscountRequest, options: Required<WriteOptions>) {
  return client.post(CONSOLE_PATHS.billingDiscounts, request, "createDiscountResponse", options);
}

/** OFFER_DISCOUNTS, at the version on screen. Pros already on it keep it. */
export function endDiscount(
  client: ConsoleClient,
  proDiscountId: string,
  request: EndDiscountRequest,
  options: Required<WriteOptions>,
) {
  return client.post(consolePath.endDiscount(proDiscountId), request, "endDiscountResponse", options);
}

/** One pro's subscriptions, newest first, with every payment and provider event. */
export function listProSubscriptions(client: ConsoleClient, workerId: string) {
  return client.get(consolePath.proSubscriptions(workerId), "listProSubscriptionsResponse");
}

/** MANAGE_SUBSCRIPTIONS. Goodwill: moves `paidThrough` later and lifts a suspension. */
export function extendSubscription(
  client: ConsoleClient,
  subscriptionId: string,
  request: ExtendSubscriptionRequest,
  options: Required<WriteOptions>,
) {
  return client.post(
    consolePath.subscriptionWrite(subscriptionId, "extend"),
    request,
    "extendSubscriptionResponse",
    options,
  );
}

/** MANAGE_SUBSCRIPTIONS. Enforcement: ends access now and stops the provider charging. */
export function endSubscription(
  client: ConsoleClient,
  subscriptionId: string,
  request: EndSubscriptionRequest,
  options: Required<WriteOptions>,
) {
  return client.post(consolePath.subscriptionWrite(subscriptionId, "end"), request, "endSubscriptionResponse", options);
}

// ── Tableau de bord ───────────────────────────────────────────────────────

/** Today in Conakry by default. A plain read. */
export function getHealthDashboard(client: ConsoleClient, request: GetHealthDashboardRequest = {}) {
  const suffix = request.day ? `?day=${encodeURIComponent(request.day)}` : "";
  return client.get(`${CONSOLE_PATHS.dashboard}${suffix}`, "getHealthDashboardResponse");
}

// ── Marketplace Operations (WALLIYO-0132) ────────────────────────────────
// Reads, all of them: the market is moved by the people on it.

const params = (query: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

export function getMarketplaceOverview(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.marketplace, "getMarketplaceOverviewResponse");
}

export function listMarketplaceJobs(client: ConsoleClient, query: ListJobsQuery = {}) {
  return client.get(`${CONSOLE_PATHS.marketplaceJobs}${params(query)}`, "listMarketplaceJobsResponse");
}

export function listMarketplaceProviders(client: ConsoleClient, query: ListProvidersQuery = {}) {
  return client.get(`${CONSOLE_PATHS.marketplaceProviders}${params(query)}`, "listMarketplaceProvidersResponse");
}

export function listMarketplaceCategories(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.marketplaceCategories, "listMarketplaceCategoriesResponse");
}

export function listMarketplaceReviews(client: ConsoleClient, query: ListMarketplaceReviewsQuery = {}) {
  return client.get(`${CONSOLE_PATHS.marketplaceReviews}${params(query)}`, "listMarketplaceReviewsResponse");
}

/**
 * Every sidebar badge, in one call.
 *
 * The sidebar is on every screen, so one request draws the whole menu rather
 * than one per row (WALLIYO-0134).
 */
export function getScreenCounts(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.screenCounts, "getScreenCountsResponse");
}

/** A department's Overview: figures, then a queue of what needs you. */
export function getWorkspaceOverview(client: ConsoleClient, department: string) {
  return client.get(`${CONSOLE_PATHS.overview}/${segment(department)}`, "getWorkspaceOverviewResponse");
}

// ── Incidents (WALLIYO-0132, #126) ───────────────────────────────────────

export function listIncidents(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.incidents, "listIncidentsResponse");
}

export function getIncident(client: ConsoleClient, incidentId: string) {
  return client.get(`${CONSOLE_PATHS.incidents}/${segment(incidentId)}`, "getIncidentResponse");
}

export function openIncident(client: ConsoleClient, request: OpenIncidentRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.incidents, request, "openIncidentResponse", options);
}

export function updateIncident(
  client: ConsoleClient,
  incidentId: string,
  request: UpdateIncidentRequest,
  options?: WriteOptions,
) {
  return client.post(`${CONSOLE_PATHS.incidents}/${segment(incidentId)}`, request, "updateIncidentResponse", options);
}

// ── Experiments and growth (WALLIYO-0132) ────────────────────────────────

export function listExperiments(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.experiments, "listExperimentsResponse");
}

export function createExperiment(client: ConsoleClient, request: CreateExperimentRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.experiments, request, "createExperimentResponse", options);
}

/** Ramping is this with a bigger share; each step is its own audit row. */
export function moveExperiment(
  client: ConsoleClient,
  experimentId: string,
  request: MoveExperimentRequest,
  options?: WriteOptions,
) {
  return client.post(
    `${CONSOLE_PATHS.experiments}/${segment(experimentId)}`,
    request,
    "moveExperimentResponse",
    options,
  );
}

export function listLeads(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.leads, "listLeadsResponse");
}

export function addLead(client: ConsoleClient, request: AddLeadRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.leads, request, "addLeadResponse", options);
}

export function moveLead(client: ConsoleClient, leadId: string, request: MoveLeadRequest, options?: WriteOptions) {
  return client.post(`${CONSOLE_PATHS.leads}/${segment(leadId)}`, request, "moveLeadResponse", options);
}

export function listCampaigns(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.campaigns, "listCampaignsResponse");
}

export function createCampaign(client: ConsoleClient, request: CreateCampaignRequest, options?: WriteOptions) {
  return client.post(CONSOLE_PATHS.campaigns, request, "createCampaignResponse", options);
}

/** Its own call: drafting costs nothing and sending spends money. */
export function sendCampaign(
  client: ConsoleClient,
  campaignId: string,
  request: SendCampaignRequest,
  options?: WriteOptions,
) {
  return client.post(
    `${CONSOLE_PATHS.campaigns}/${segment(campaignId)}/send`,
    request,
    "sendCampaignResponse",
    options,
  );
}

export function listReferrals(client: ConsoleClient) {
  return client.get(CONSOLE_PATHS.referrals, "listReferralsResponse");
}
